import React, { useState } from 'react';
import './GuideStyles.css';

const HowToDisconnectZoom = ({ onBack }) => {
    const [modalImage, setModalImage] = useState(null);

    const handleImageClick = (src, alt) => {
        setModalImage({ src, alt });
    };

    const closeModal = () => {
        setModalImage(null);
    };

    return (
        <div className="guide-container">
            <button className="back-button" onClick={onBack}>
                ← Back to Documentation
            </button>

            <div className="guide-header">
                <h1>How to Remove Zoom Integration</h1>
                <p className="guide-subtitle">Step-by-step guide to disconnect your Zoom account from ClientBloom</p>
            </div>

            <div className="guide-content">
                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 1: Access the Admin Panel</h3>
                        <p>Log in to ClientBloom and navigate to the <strong>Admin Panel</strong> from the sidebar menu.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="go_admin_panel.png" 
                                alt="Go to Admin Panel" 
                                onClick={() => handleImageClick('go_admin_panel.png', 'Go to Admin Panel')}
                                className="clickable-image"
                            />
                        </div>
                    </div>
                </div>

                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 2: Go to Integrations</h3>
                        <p>In the Admin Panel, click on the <strong>"Integrations"</strong> tab.</p>
                        <div className="screenshot-placeholder">
                             <img 
                                src="admin_panel.png" 
                                alt="Admin Panel" 
                                onClick={() => handleImageClick('admin_panel.png', 'Admin Panel')}
                                className="clickable-image"
                            />
                        </div>
                    </div>
                </div>

                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 3: Find the Zoom Integration</h3>
                        <p>Scroll to the Zoom card. If connected, you'll see the <strong>"Disconnect"</strong> and <strong>"Manage"</strong> buttons displayed.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="search_zoom_connected.PNG" 
                                alt="Search Zoom Connected Integration" 
                                onClick={() => handleImageClick('search_zoom_connected.PNG', 'Search Zoom Connected Integration')}
                                className="clickable-image"
                            />
                        </div>
                    </div>
                </div>

                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 4: Click "Disconnect"</h3>
                        <p>Press the red <strong>"Disconnect"</strong> button on the Zoom card.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="zoom_connected.PNG" 
                                alt="Zoom Connected Integration" 
                                onClick={() => handleImageClick('zoom_connected.PNG', 'Zoom Connected Integration')}
                                className="clickable-image"
                            />
                        </div>
                        <div className="warning-box">
                            <strong>⚠️ Important:</strong> This action will immediately disconnect the integration and revoke access permissions.
                        </div>
                    </div>
                </div>

                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 5: Confirm Disconnection</h3>
                        <p>A confirmation dialog will appear. Read the message and click <strong>"Disconnect"</strong> to proceed.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="zoom_disconnected.PNG" 
                                alt="Zoom Disconnect Confirmation" 
                                onClick={() => handleImageClick('zoom_disconnected.PNG', 'Zoom Disconnect Confirmation')}
                                className="clickable-image"
                            />
                        </div>
                    </div>
                </div>

                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 6: Successful Disconnection</h3>
                        <p>You'll see a success notification and the Zoom card status will change to <strong>"Not Connected"</strong>.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="zoom_disconnected_successfully.png" 
                                alt="Zoom Disconnect Successfully" 
                                onClick={() => handleImageClick('zoom_disconnected_successfully.png', 'Zoom Disconnect Successfully')}
                                className="clickable-image"
                            />
                        </div>
                        <div className="success-box">
                            <strong>✅ Done!</strong> Your Zoom account has been successfully disconnected from ClientBloom.
                        </div>
                    </div>
                </div>

                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 7: Access Zoom Marketplace</h3>
                        <p>Go to <a href="https://marketplace.zoom.us/" target="_blank" rel="noopener noreferrer"><strong>https://marketplace.zoom.us/</strong></a> and sign in with your Zoom account.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="zoom_marketplace.png" 
                                alt="Zoom Marketplace Login" 
                                onClick={() => handleImageClick('zoom_marketplace.png', 'Zoom Marketplace Login')}
                                className="clickable-image"
                            />
                        </div>
                    </div>
                </div>

                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 8: Navigate to Manage</h3>
                        <p>Once logged in, click on the <strong>"Manage"</strong> option in the top menu.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="zoom_marketplace_manage.png" 
                                alt="Zoom Marketplace Manage" 
                                onClick={() => handleImageClick('zoom_marketplace_manage.png', 'Zoom Marketplace Manage')}
                                className="clickable-image"
                            />
                        </div>
                    </div>
                </div>

                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 9: Go to Added Apps</h3>
                        <p>In the Manage section, select <strong>"Added Apps"</strong> to view all installed applications.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="zoom_added_apps.png" 
                                alt="Zoom Added Apps" 
                                onClick={() => handleImageClick('zoom_added_apps.png', 'Zoom Added Apps')}
                                className="clickable-image"
                            />
                        </div>
                    </div>
                </div>

                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 10: Find ClientBloom Integration</h3>
                        <p>Scroll through the list and locate <strong>"ClientBloom Integration"</strong>.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="zoom_find_clientbloom.png" 
                                alt="Find ClientBloom App" 
                                onClick={() => handleImageClick('zoom_find_clientbloom.png', 'Find ClientBloom App')}
                                className="clickable-image"
                            />
                        </div>
                    </div>
                </div>

                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 11: Remove the App</h3>
                        <p>Click on the <strong>three dots (⋮)</strong> next to ClientBloom Integration and select <strong>"Remove"</strong>.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="zoom_remove_app.png" 
                                alt="Remove ClientBloom App" 
                                onClick={() => handleImageClick('zoom_remove_app.png', 'Remove ClientBloom App')}
                                className="clickable-image"
                            />
                        </div>
                    </div>
                </div>

                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 12: Confirm Removal</h3>
                        <p>A confirmation dialog will appear. Click the <strong>"Remove"</strong> button to completely uninstall the app from your Zoom account.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="remove_app.png" 
                                alt="Confirm Remove App" 
                                onClick={() => handleImageClick('remove_app.png', 'Confirm Remove App')}
                                className="clickable-image"
                            />
                        </div>
                        <div className="success-box">
                            <strong>✅ Complete!</strong> The ClientBloom Integration has been fully removed from your Zoom account.
                        </div>
                    </div>
                </div>

                <div className="guide-section">
                    <h2>What Happens After Disconnecting?</h2>
                    <div className="info-section">
                        <div className="info-item">
                            <h4>🔒 Access Revoked</h4>
                            <p>ClientBloom will no longer be able to access your recordings or Zoom data.</p>
                        </div>
                        <div className="info-item">
                            <h4>🔑 Tokens Deleted</h4>
                            <p>OAuth tokens (access and refresh) will be permanently deleted from our database.</p>
                        </div>
                        <div className="info-item">
                            <h4>🛑 Sync Stopped</h4>
                            <p>New recordings will no longer sync automatically.</p>
                        </div>
                        <div className="info-item">
                            <h4>💾 Historical Data</h4>
                            <p>Previously synced calls and transcriptions will remain in your ClientBloom account.</p>
                        </div>
                    </div>
                </div>

                <div className="guide-section">
                    <h2>Deleting Historical Data</h2>
                    <div className="warning-section">
                        <p>If you want to completely delete previously synced Zoom data:</p>
                        <ul>
                            <li><strong>Option 1:</strong> Delete individual calls from the "Calls" section in ClientBloom</li>
                            <li><strong>Option 2:</strong> Contact support to delete all Zoom-related data: <a href="mailto:hello@clientbloom.ai">hello@clientbloom.ai</a></li>
                        </ul>
                    </div>
                </div>

                <div className="guide-section">
                    <h2>Reconnection</h2>
                    <div className="info-box">
                        <p>You can reconnect your Zoom account at any time by following the integration process again. Keep in mind that:</p>
                        <ul>
                            <li>New recordings will sync after reconnecting</li>
                            <li>Recordings from the period you were disconnected will NOT sync automatically</li>
                            <li>Historical data from before disconnection remains intact</li>
                        </ul>
                    </div>
                </div>

                <div className="guide-section">
                    <h2>Need Help?</h2>
                    <div className="support-section">
                        <p>If you have problems disconnecting the integration, contact our support team:</p>
                        <ul>
                            <li><strong>Email:</strong> <a href="mailto:hello@clientbloom.ai">hello@clientbloom.ai</a></li>
                            <li><strong>Include:</strong> Your Workspace Name, Zoom account email, and error screenshots (if applicable)</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Image Modal */}
            {modalImage && (
                <div className="image-modal" onClick={closeModal}>
                    <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeModal}>×</button>
                        <img src={modalImage.src} alt={modalImage.alt} />
                        <p className="modal-caption">{modalImage.alt}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HowToDisconnectZoom;
